"use strict";
/**
 * Weekly Report V2 — Section Content Types
 *
 * Zod-first shared data contract between backend (JSONB) and frontend (rendering).
 * 8 sections: Cover, Skin Snapshot, Apparent Age, Notable Changes, Routine, Suggestions, Context, Close.
 * Each section: Zod schema -> inferred type. All dates as ISO strings.
 *
 * Shared sub-schemas (TrendDirection, TimeSeriesPoint, UniversalMetricTile,
 * ChartAnnotation, DetailMetricChart) are defined here and imported by detailData.ts.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.WeeklyReportListItemSchema = exports.WeeklyReportResponseSchema = exports.RenderedSectionSchema = exports.SectionSchemaVersionSchema = exports.SectionContentSchema = exports.DataQualityContentSchema = exports.QualityTierSchema = exports.CloseContentSchema = exports.GoalReflectionSchema = exports.LifestyleInsightsContentSchema = exports.ContextContentSchema = exports.SuggestionsContentSchema = exports.SuggestionProductRecommendationSchema = exports.SuggestionScheduleSchema = exports.RoutineContentSchema = exports.LifestyleConfoundSchema = exports.SuggestionCardItemSchema = exports.NotableChangesContentSchema = exports.LifestyleAnnotationSchema = exports.ApparentAgeContentSchema = exports.AgeEstimateSchema = exports.SkinSnapshotContentSchema = exports.CoverContentSchema = exports.DetailMetricChartSchema = exports.ChartAnnotationSchema = exports.UniversalMetricTileSchema = exports.TimeSeriesPointSchema = exports.TrendDirectionSchema = exports.AreaSchema = exports.TechNameSchema = void 0;
const zod_1 = require("zod");
const enums_1 = require("../api/enums");
const skin_goals_1 = require("../api/skin_goals");
const enums_2 = require("./enums");
const hook_1 = require("./hook");
const motion_1 = require("./motion");
const skinThisWeek_1 = require("./skinThisWeek");
// ============================================================================
// ZOD ENUMS FOR TECH NAME + AREA
// ============================================================================
exports.TechNameSchema = zod_1.z.enum(enums_1.techNameEnum);
exports.AreaSchema = zod_1.z.enum(enums_1.areaEnum);
// ============================================================================
// SHARED SUB-SCHEMAS
// ============================================================================
/** Trend direction for metric movement */
exports.TrendDirectionSchema = zod_1.z.enum([
    'improving',
    'stable',
    'declining',
    'fluctuating',
]);
/** A single time series data point for charts */
exports.TimeSeriesPointSchema = zod_1.z.object({
    date: zod_1.z.string(),
    score: zod_1.z.number(),
});
/**
 * Universal metric tile — reused across S2 metric groups, product secondary
 * metrics, treatment secondary metrics, and metric regional breakdown.
 */
exports.UniversalMetricTileSchema = zod_1.z.object({
    techName: exports.TechNameSchema,
    area: exports.AreaSchema,
    displayName: zod_1.z.string(),
    score: zod_1.z.number(),
    trend: exports.TrendDirectionSchema,
    trendLabel: zod_1.z.string(),
    sparklinePoints: zod_1.z.array(zod_1.z.number()),
    /** Fast EMA sparkline points — only present when a treatment is active for this metric */
    fastEmaPoints: zod_1.z.array(zod_1.z.number()).optional(),
    hasMcidCrossing: zod_1.z.boolean(),
});
/** Chart annotation marking routine events on a time series */
exports.ChartAnnotationSchema = zod_1.z.object({
    type: zod_1.z.enum(['product_start', 'frequency_change', 'treatment_date']),
    date: zod_1.z.string(),
    label: zod_1.z.string(),
});
/**
 * Detail metric chart — shared by product primaryMetrics and treatment
 * targetMetrics on detail screens.
 */
exports.DetailMetricChartSchema = zod_1.z.object({
    techName: exports.TechNameSchema,
    area: exports.AreaSchema,
    displayName: zod_1.z.string(),
    trend: exports.TrendDirectionSchema,
    trendLabel: zod_1.z.string(),
    percentChange: zod_1.z.number().nullable(),
    timeSeries: zod_1.z.array(exports.TimeSeriesPointSchema),
    /** Fast EMA time series — only present when a treatment is active for this metric */
    fastEmaSeries: zod_1.z.array(exports.TimeSeriesPointSchema).optional(),
    baselineScore: zod_1.z.number(),
    currentScore: zod_1.z.number(),
    annotations: zod_1.z.array(exports.ChartAnnotationSchema),
    observationProse: zod_1.z.string(),
    // Signal scoring — aggregated counts within the report period
    acuteSignalCount: zod_1.z.number(),
    mcidCrossingCount: zod_1.z.number(),
    alertScore: zod_1.z.number(),
});
// ============================================================================
// S1: COVER
// ============================================================================
exports.CoverContentSchema = zod_1.z.object({
    heroHeadline: zod_1.z.string(),
    heroSubline: zod_1.z.string(),
    weekLabel: zod_1.z.string(),
    reportDate: zod_1.z.string(),
    metricCount: zod_1.z.number(),
    weeksTracked: zod_1.z.number(),
    /**
     * V3 hook callout rendered under the cover hero. Absent on historical V2
     * reports; present on new reports generated after the V3 cover ships.
     * The frontend branches its cover layout on the presence of this field.
     */
    hookObservation: hook_1.HookObservationSchema.optional(),
});
// ============================================================================
// S2: SKIN SNAPSHOT
// ============================================================================
exports.SkinSnapshotContentSchema = zod_1.z.object({
    holisticOverview: zod_1.z.string(),
    heroMetric: zod_1.z.object({
        techName: exports.TechNameSchema,
        displayName: zod_1.z.string(),
        area: exports.AreaSchema,
        score: zod_1.z.number(),
        trend: exports.TrendDirectionSchema,
        observation: zod_1.z.string(),
    }),
    metricGroups: zod_1.z.array(zod_1.z.object({
        groupName: zod_1.z.string(),
        groupStatus: exports.TrendDirectionSchema,
        isDefaultOpen: zod_1.z.boolean(),
        metrics: zod_1.z.array(exports.UniversalMetricTileSchema),
    })),
    /**
     * V3 goal check-in card. Absent on historical V2 reports; present on new
     * reports generated after the V3 Beat 2 ships.
     */
    goalCheckIn: skinThisWeek_1.GoalCheckInSchema.optional(),
    /**
     * V3 latest in-period scan image + dimensions. Drives the FaceScanCarousel:
     * every slide renders this image with the slide's per-metric regional
     * overlay (markers positioned via the landmark coords on each
     * ChangeListItemRegion). Absent on historical V2 reports.
     */
    latestScan: zod_1.z
        .object({
        scanId: zod_1.z.string().optional(),
        imageUrl: zod_1.z.string(),
        imageWidth: zod_1.z.number(),
        imageHeight: zod_1.z.number(),
    })
        .optional(),
    /**
     * V3 curated focus metrics for the week. Powers both the face-scan
     * carousel (1 slide per item) and the "What Changed" list. 2-4 items.
     * Absent on historical V2 reports — those continue to render
     * `notable_changes` as a separate section and use this section's v2
     * layout.
     */
    changes: zod_1.z.array(skinThisWeek_1.ChangeListItemSchema).max(4).optional(),
});
// ============================================================================
// S2.5: APPARENT AGE
// ============================================================================
/** Age estimate for a single zone (skin or eyes) */
exports.AgeEstimateSchema = zod_1.z.object({
    ageYears: zod_1.z.number(),
    trend: exports.TrendDirectionSchema,
    sparklinePoints: zod_1.z.array(zod_1.z.number()),
});
exports.ApparentAgeContentSchema = zod_1.z.object({
    skinAge: exports.AgeEstimateSchema,
    eyeAge: exports.AgeEstimateSchema,
    chronologicalAge: zod_1.z.number().nullable(),
    skinAgeMarkers: zod_1.z.array(exports.UniversalMetricTileSchema),
    eyeAgeMarkers: zod_1.z.array(exports.UniversalMetricTileSchema),
});
// ============================================================================
// S3: NOTABLE CHANGES
// ============================================================================
/** Lifestyle annotation for a notable metric change — wearable signal correlated with the change */
exports.LifestyleAnnotationSchema = zod_1.z.object({
    metric: zod_1.z.enum(enums_1.techNameEnum),
    signal: zod_1.z.string(),
    signalDisplayName: zod_1.z.string(),
    deviationPercent: zod_1.z.number(),
    skinContext: zod_1.z.string(),
    source: zod_1.z.string(),
});
exports.NotableChangesContentSchema = zod_1.z.object({
    pullQuote: zod_1.z.object({
        text: zod_1.z.string(),
        accentSpan: zod_1.z.string(),
    }),
    detail: zod_1.z.string(),
    chart: zod_1.z.object({
        techName: exports.TechNameSchema,
        area: exports.AreaSchema,
        timeSeries: zod_1.z.array(exports.TimeSeriesPointSchema),
        baselineScore: zod_1.z.number(),
        currentScore: zod_1.z.number(),
        timeRangeLabel: zod_1.z.string(),
        direction: exports.TrendDirectionSchema,
        fastEmaSeries: zod_1.z.array(exports.TimeSeriesPointSchema).optional(),
    }),
    emerging: zod_1.z
        .object({
        techName: exports.TechNameSchema.optional(),
        description: zod_1.z.string(),
        expectedTimeline: zod_1.z.string(),
    })
        .nullable(),
    watching: zod_1.z.array(zod_1.z.object({
        techName: exports.TechNameSchema,
        area: exports.AreaSchema,
    })),
    /** Lifestyle annotations: wearable signals correlated with notable metric changes */
    lifestyleAnnotations: zod_1.z.array(exports.LifestyleAnnotationSchema).optional(),
});
// ============================================================================
// S5: SUGGESTIONS — card item shape (defined ahead of S4 because the
// "Try next" carousel on the consolidated routine section reuses it).
// ============================================================================
exports.SuggestionCardItemSchema = zod_1.z.object({
    suggestionId: zod_1.z.number(),
    suggestionType: zod_1.z.string(),
    headline: zod_1.z.string(),
    body: zod_1.z.string(),
    /** Product ID this suggestion targets (frequency/timing/simplification). Null for add_ingredient. */
    productId: zod_1.z.number().nullable().optional(),
    scheduleComparison: zod_1.z
        .object({
        currentLabel: zod_1.z.string(),
        suggestedLabel: zod_1.z.string(),
    })
        .nullable(),
    /** For add_ingredient: raw ingredient category key (e.g., "retinoid", "vitamin_c") */
    ingredientCategory: zod_1.z.string().optional(),
    /** For add_ingredient: human-readable display name (e.g., "Retinoid", "Vitamin C") */
    ingredientDisplayName: zod_1.z.string().optional(),
});
// ============================================================================
// S4: ROUTINE
// ============================================================================
/** Lifestyle confound annotation for a routine product — wearable signal overlaps product target metric */
exports.LifestyleConfoundSchema = zod_1.z.object({
    productName: zod_1.z.string(),
    signal: zod_1.z.string(),
    signalDisplayName: zod_1.z.string(),
    deviationPercent: zod_1.z.number(),
    direction: zod_1.z.enum(['declining', 'rising']),
    skinContext: zod_1.z.string(),
    overlappingMetric: zod_1.z.enum(enums_1.techNameEnum),
    source: zod_1.z.string(),
});
exports.RoutineContentSchema = zod_1.z.object({
    products: zod_1.z.array(zod_1.z.object({
        productId: zod_1.z.number(),
        productName: zod_1.z.string(),
        headlineStatus: zod_1.z.string(),
        routineNote: zod_1.z.string().nullable(),
        sortPriority: zod_1.z.number(),
    })),
    treatments: zod_1.z.array(zod_1.z.object({
        treatmentId: zod_1.z.number(),
        treatmentSlug: zod_1.z.enum(enums_1.treatmentSlugEnum),
        treatmentName: zod_1.z.string(),
        treatmentType: zod_1.z.string(),
        headlineStatus: zod_1.z.string(),
    })),
    /** Lifestyle confounds: wearable signal changes overlapping with product target metrics */
    lifestyleConfounds: zod_1.z.array(exports.LifestyleConfoundSchema).optional(),
    /**
     * V3 Beat 3 — Conclusions carousel. Closed observation windows with verdict
     * + read. Optional so historical reports keep their existing routine layout.
     */
    conclusions: zod_1.z.array(motion_1.WindowConclusionSchema).optional(),
    /**
     * V3 Beat 3 — In Progress carousel. Active observation windows.
     * Optional for backward compatibility with historical reports.
     */
    activeTries: zod_1.z.array(motion_1.ActiveTrySchema).optional(),
    /**
     * V3 Beat 3 — Counts of products + treatments NOT surfaced as conclusions
     * or active tries. Renders under the "See your full routine" link.
     */
    stableCount: motion_1.StableRoutineCountSchema.optional(),
    /**
     * Curated, conflict-filtered suggestions for the "Try next" carousel
     * on the consolidated routine section. Reuses SuggestionCardItemSchema
     * (the same shape the V2 standalone suggestions section uses) so the
     * carousel cards consume display strings produced by the existing
     * buildSuggestionContentMap() pipeline. Defaults to [] so V2 legacy
     * reports persisted before this field round-trip cleanly.
     *
     * Filtered server-side via `filterConflictingSuggestions` to drop
     * simplification on a keep_using product and any suggestion targeting
     * an active-try product. See
     * `docs/superpowers/specs/2026-05-01-routine-section-consolidation-design.md`.
     */
    tryNext: zod_1.z.array(exports.SuggestionCardItemSchema).default([]),
});
// ============================================================================
// SUGGESTION TABLE DATA TYPES
// ============================================================================
/** Schedule for a suggested routine change (stored in wr_suggestion JSONB) */
exports.SuggestionScheduleSchema = zod_1.z.object({
    daysPerWeek: zod_1.z.number().min(1).max(7),
    periods: zod_1.z.array(zod_1.z.string()),
    slots: zod_1.z
        .array(zod_1.z.object({
        day: zod_1.z.enum(enums_1.dayOfWeekEnum),
        period: zod_1.z.enum(enums_1.periodEnum),
    }))
        .optional(),
});
/** Product recommendation attached to a suggestion (stored in wr_suggestion_product) */
exports.SuggestionProductRecommendationSchema = zod_1.z.object({
    productId: zod_1.z.number(),
    title: zod_1.z.string(),
    brandName: zod_1.z.string(),
    imageUrl: zod_1.z.string().nullable(),
    keyIngredients: zod_1.z.array(zod_1.z.string()).optional(),
});
// ============================================================================
// S5: SUGGESTIONS
// ============================================================================
exports.SuggestionsContentSchema = zod_1.z.object({
    suggestions: zod_1.z.array(exports.SuggestionCardItemSchema),
});
// ============================================================================
// S6: CONTEXT
// ============================================================================
exports.ContextContentSchema = zod_1.z.object({
    observations: zod_1.z.array(zod_1.z.object({
        type: zod_1.z.enum(['seasonal', 'behavioral', 'environmental', 'lifestyle']),
        body: zod_1.z.string(),
    })),
});
// ============================================================================
// S6.5: LIFESTYLE INSIGHTS
// ============================================================================
/**
 * Lifestyle Insights section content — Chapter 04 (Lifestyle & Skin).
 * Persisted as JSONB in `wr_section`. Contains everything the frontend needs
 * to render signal cards, sparklines, correlated metrics, and compound flags.
 */
exports.LifestyleInsightsContentSchema = zod_1.z.object({
    /** The curated editorial narrative (2-3 sentences) */
    prose: zod_1.z.string(),
    /** Whether the prose was generated by LLM or template fallback */
    usedFallback: zod_1.z.boolean(),
    /** Selected lifestyle signals with data for card rendering */
    signals: zod_1.z.array(zod_1.z.object({
        signal: zod_1.z.string(),
        displayName: zod_1.z.string(),
        medicalBand: zod_1.z.enum(['low', 'reduced', 'normal', 'elevated']),
        deviationPercent: zod_1.z.number().nullable(),
        direction: zod_1.z.enum(['declining', 'stable', 'rising']),
        acuteMedian: zod_1.z.number(),
        baselineMedian: zod_1.z.number().nullable(),
        unit: zod_1.z.string(),
        /** 7-day daily values for sparkline rendering */
        dailyValues: zod_1.z.array(zod_1.z.object({
            date: zod_1.z.string(),
            value: zod_1.z.number(),
        })),
        /** Bridge text — why this signal matters for skin (from medical reference bands) */
        skinContext: zod_1.z.string(),
        /** Per-bucket LLM-generated narrative about this signal's skin connection */
        bucketProse: zod_1.z.string().optional(),
        /** Skin metrics with a detected concordant change this week */
        correlatedMetrics: zod_1.z.array(zod_1.z.object({
            metric: zod_1.z.enum(enums_1.techNameEnum),
            direction: zod_1.z.enum(['improvement', 'worsening']),
        })),
        /** All evidence-map-linked metrics with their status (matched or watching) */
        linkedMetrics: zod_1.z.array(zod_1.z.object({
            metric: zod_1.z.enum(enums_1.techNameEnum),
            /** 'matched' = concordant change detected; 'watching' = no concordant change yet */
            status: zod_1.z.enum(['matched', 'watching']),
        })),
        /** Supporting context values for this bucket (e.g., sleep quality, exercise minutes) */
        supportingValues: zod_1.z
            .array(zod_1.z.object({
            label: zod_1.z.string(),
            value: zod_1.z.number(),
            unit: zod_1.z.string(),
        }))
            .optional(),
        /** Product context: a routine product that also targets one of the signal's linked metrics */
        productContext: zod_1.z
            .object({
            productName: zod_1.z.string(),
            targetMetric: zod_1.z.string(),
            observationNote: zod_1.z.string(),
        })
            .nullable()
            .optional(),
    })),
    /** Compound signal flag (shown as yellow callout when 2+ signals degraded) */
    compoundFlag: zod_1.z
        .object({
        count: zod_1.z.number(),
        context: zod_1.z.string(),
    })
        .nullable(),
    /** Signals assessed as stable — shown in Context section, not here */
    quietSignals: zod_1.z.array(zod_1.z.object({
        signal: zod_1.z.string(),
        displayName: zod_1.z.string(),
        context: zod_1.z.string(),
    })),
    /** Wearable connection status at report generation time. 'none' = no connection, 'connected' = connection exists but insufficient data. Absent when signals are populated. */
    connectionStatus: zod_1.z.enum(['none', 'connected']).optional(),
    /** Wearable provider at report generation time (e.g. 'oura', 'apple_health'). Null when no connection. */
    connectedProvider: zod_1.z.string().nullable().optional(),
});
// ============================================================================
// S7: CLOSE
// ============================================================================
exports.GoalReflectionSchema = zod_1.z.object({
    currentGoal: zod_1.z.string(),
    goalExplanation: zod_1.z.string(),
});
exports.CloseContentSchema = zod_1.z.object({
    closeProse: zod_1.z.string(),
    keepScanningPrompt: zod_1.z.string(),
    nextReportDate: zod_1.z.string(),
    goalReflections: zod_1.z.array(exports.GoalReflectionSchema).max(skin_goals_1.MAX_SKIN_GOALS),
});
// ============================================================================
// DATA QUALITY
// ============================================================================
exports.QualityTierSchema = zod_1.z.enum([
    'excellent',
    'good',
    'fair',
    'needs_improvement',
]);
exports.DataQualityContentSchema = zod_1.z.object({
    averageScore: zod_1.z.number(),
    scanCount: zod_1.z.number(),
    qualityTier: exports.QualityTierSchema,
});
// ============================================================================
// DISCRIMINATED UNION — TYPE-SAFE SECTION CONTENT
// ============================================================================
exports.SectionContentSchema = zod_1.z.discriminatedUnion('section', [
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.COVER),
        content: exports.CoverContentSchema,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.SKIN_SNAPSHOT),
        content: exports.SkinSnapshotContentSchema,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.APPARENT_AGE),
        content: exports.ApparentAgeContentSchema,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.NOTABLE_CHANGES),
        content: exports.NotableChangesContentSchema,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.ROUTINE),
        content: exports.RoutineContentSchema,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.SUGGESTIONS),
        content: exports.SuggestionsContentSchema,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.DATA_QUALITY),
        content: exports.DataQualityContentSchema,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.CONTEXT),
        content: exports.ContextContentSchema,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.LIFESTYLE_INSIGHTS),
        content: exports.LifestyleInsightsContentSchema,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.CLOSE),
        content: exports.CloseContentSchema,
    }),
]);
// ============================================================================
// API RESPONSE — CONSUMED BY FRONTEND
// ============================================================================
/** Zod schema for section schema version — null/undefined = pre-versioning era (treat as V2) */
exports.SectionSchemaVersionSchema = zod_1.z.union([zod_1.z.literal(2), zod_1.z.literal(3)]);
/** Shared fields present on every rendered section */
const renderedSectionBase = {
    schemaVersion: exports.SectionSchemaVersionSchema.optional(),
    contentSource: zod_1.z.enum(enums_2.contentSourceEnum),
    renderOrder: zod_1.z.number(),
};
/** A rendered section — discriminated union gives typed content per section */
exports.RenderedSectionSchema = zod_1.z.discriminatedUnion('section', [
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.COVER),
        content: exports.CoverContentSchema,
        ...renderedSectionBase,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.SKIN_SNAPSHOT),
        content: exports.SkinSnapshotContentSchema,
        ...renderedSectionBase,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.APPARENT_AGE),
        content: exports.ApparentAgeContentSchema,
        ...renderedSectionBase,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.NOTABLE_CHANGES),
        content: exports.NotableChangesContentSchema,
        ...renderedSectionBase,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.ROUTINE),
        content: exports.RoutineContentSchema,
        ...renderedSectionBase,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.SUGGESTIONS),
        content: exports.SuggestionsContentSchema,
        ...renderedSectionBase,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.DATA_QUALITY),
        content: exports.DataQualityContentSchema,
        ...renderedSectionBase,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.CONTEXT),
        content: exports.ContextContentSchema,
        ...renderedSectionBase,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.LIFESTYLE_INSIGHTS),
        content: exports.LifestyleInsightsContentSchema,
        ...renderedSectionBase,
    }),
    zod_1.z.object({
        section: zod_1.z.literal(enums_2.ReportSection.CLOSE),
        content: exports.CloseContentSchema,
        ...renderedSectionBase,
    }),
]);
/** Full weekly report API response */
exports.WeeklyReportResponseSchema = zod_1.z.object({
    report: zod_1.z.object({
        id: zod_1.z.number(),
        userId: zod_1.z.string(),
        reportMode: zod_1.z.enum(enums_2.reportModeEnum),
        reportVariant: zod_1.z.enum(enums_2.reportVariantEnum),
        status: zod_1.z.enum(enums_2.weeklyReportStatusEnum),
        /**
         * Why a `skipped` report exists rather than a generated one. Null for
         * `ready`/non-skipped reports. `subscription_required` → the frontend
         * renders an informational skip card instead of report content.
         */
        skipReason: zod_1.z.enum(enums_2.weeklyReportSkipReasonEnum).nullish(),
        reportDate: zod_1.z.string(),
        weekNumber: zod_1.z.number().nullable(),
        periodStart: zod_1.z.string(),
        periodEnd: zod_1.z.string(),
        scanCount: zod_1.z.number(),
    }),
    sections: zod_1.z.array(exports.RenderedSectionSchema),
    /** Suggestion IDs that have been accepted by the user for this report. */
    acceptedSuggestionIds: zod_1.z.array(zod_1.z.number()),
});
/** List item for the weekly report list endpoint */
exports.WeeklyReportListItemSchema = zod_1.z.object({
    id: zod_1.z.number(),
    reportMode: zod_1.z.enum(enums_2.reportModeEnum),
    reportVariant: zod_1.z.enum(enums_2.reportVariantEnum),
    status: zod_1.z.enum(enums_2.weeklyReportStatusEnum),
    /**
     * Why a `skipped` report exists rather than a generated one. Null for
     * `ready`/non-skipped reports. `subscription_required` → the list renders
     * an informational skip-card variant instead of a normal report card.
     */
    skipReason: zod_1.z.enum(enums_2.weeklyReportSkipReasonEnum).nullish(),
    reportDate: zod_1.z.string(),
    weekNumber: zod_1.z.number().nullable(),
    periodStart: zod_1.z.string(),
    periodEnd: zod_1.z.string(),
    scanCount: zod_1.z.number(),
    coverHeadline: zod_1.z.string().nullable(),
    createdAt: zod_1.z.string(),
});
//# sourceMappingURL=sectionContent.js.map